
import React from 'react';
import { Bell, LogOut, Home, Bot, Star, BarChart3, Wallet, BotMessageSquare, Settings, UserPlus } from 'lucide-react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { TopbarLanguageDropdown } from '../common/TopbarLanguageDropdown';
import { BetaBadge } from '../common/BetaBadge';
import { useTranslation } from "react-i18next";
import { useIsMobile } from '@/hooks/use-mobile';
import { useIsActiveAffiliate } from '@/hooks/useIsActiveAffiliate';

export const AppTopbar = () => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { hasAffiliateRecord } = useIsActiveAffiliate();

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success(t('logout'));
    } catch (error) {
      console.error("Error during logout:", error);
      toast.error(t('logout') + " failed");
    }
  };

  const baseMenuItems = [
    { label: t('dashboard'), icon: Home, path: '/' },
    { label: t('my_bots'), icon: Bot, path: '/my-bots' },
    { label: t('premium_bots'), icon: Star, path: '/marketplace' },
    { label: t('prop_trading_bots'), icon: BarChart3, path: '/prop-trading' },
    { label: t('exchange_accounts'), icon: Wallet, path: '/exchange-accounts' },
    { label: t('build_your_bot_with_ai'), icon: BotMessageSquare, path: '/community' },
  ];

  const affiliateMenuItem = hasAffiliateRecord 
    ? { label: t('affiliate_program'), icon: Star, path: '/affiliate' }
    : { label: t('become_a_partner'), icon: UserPlus, path: '/affiliate/onboarding' };

  const menuItems = [
    ...baseMenuItems,
    affiliateMenuItem,
    { label: t('settings'), icon: Settings, path: '/settings' },
  ];

  return (
    <header className="h-16 flex items-center justify-between px-6 border-b border-border bg-background">
      <div className="flex items-center">
        <h1 className="text-xl font-bold flex items-center">
          {t("dashboard")}
          <BetaBadge />
        </h1>
      </div>

      <div className="flex items-center space-x-4">
        <TopbarLanguageDropdown />

        {!isMobile && (
          <Button variant="ghost" size="icon">
            <Bell size={20} />
          </Button>
        )}
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/20 text-primary-foreground">
                  {user?.user_metadata?.name?.split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase() || 'TB'}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel>{t("my_account")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {menuItems.map((item) => (
              <DropdownMenuItem key={item.path} asChild>
                <Link 
                  to={item.path}
                  className={location.pathname === item.path ? 'bg-accent' : ''}
                >
                  <item.icon className="mr-2 h-4 w-4" />
                  {item.label}
                </Link>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-500 cursor-pointer">
              <LogOut className="mr-2 h-4 w-4" />
              {t("log_out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
